/**
 * SEO pagination helpers for listing pages (PLP / SLP).
 *
 * Listing pages load products through infinite scroll, so a crawler that does
 * not execute JS only ever sees the first chunk of results. To make the full
 * list crawlable we back the infinite scroll with a paginated series of
 * component pages, as recommended by
 * https://developers.google.com/search/blog/2014/02/infinite-scroll-search-friendly
 *
 * The series is exposed through a single 1-based `page` query param:
 *
 *   /c/8302030001            -> page 1 (canonical entry, no param)
 *   /c/8302030001?page=2     -> products 46-90
 *
 * `page` is translated to the API's 0-based `currentPage` and always paired
 * with a fixed `pageSize` (SEO_PAGE_SIZE) so a given URL returns the same
 * products for every device. Without this the same URL would resolve to a
 * different slice on mobile (15 per page) and desktop (45 per page).
 */
// No `url` import on purpose: this module is also pulled into the client
// bundle, and webpack would have to polyfill the node core module for one
// pathname split.
const qs = require('qs');

// Fixed page size for every `?page=N` request, on all devices.
export const SEO_PAGE_SIZE = 45;

export const SEO_PAGE_PARAM = 'page';

// Hard ceiling on the accepted page number. Anything above this is bounced to
// page 1 instead of being proxied to the API, so a malformed link cannot turn
// into an unbounded crawl space.
const MAX_SEO_PAGE = 9999;

// How many numbered links (excluding first/last) the hidden nav renders around
// the current page.
const PAGE_WINDOW = 2;

/**
 * Params that must never appear in a generated series URL. They do not identify
 * the result set, so leaving them in would hand crawlers a separate copy of the
 * series per campaign / experiment arm.
 * - classifier: injected server side (searchMobile mutates req.query), so it is
 *   not even part of the URL the user is on.
 * - searchAbFlagObj: A/B payload - crawlers should see the default experience.
 * - itm_*, utm_*: campaign tracking.
 */
const NON_CANONICAL_PARAMS = ['classifier', 'searchAbFlagObj'];
const NON_CANONICAL_PREFIXES = ['itm_', 'utm_'];

function stripNonCanonicalParams(query = {}) {
  return Object.keys(query).reduce((acc, key) => {
    if (NON_CANONICAL_PARAMS.includes(key)) {
      return acc;
    }
    if (NON_CANONICAL_PREFIXES.some(prefix => key.startsWith(prefix))) {
      return acc;
    }
    acc[key] = query[key];
    return acc;
  }, {});
}

/**
 * Parse the incoming `page` query param.
 *
 * @returns {{page: number, isPaginated: boolean, needsRedirect: boolean}}
 *  page           - normalised 1-based page number
 *  isPaginated    - true when the request targets page 2 or beyond
 *  needsRedirect  - true when the URL should 301 to the clean (page 1) URL,
 *                   i.e. `?page=1`, `?page=0`, `?page=abc`, `?page=1e3`...
 */
export function parseSeoPage(rawPage) {
  if (rawPage === undefined || rawPage === null || rawPage === '') {
    return { page: 1, isPaginated: false, needsRedirect: false };
  }
  const raw = String(Array.isArray(rawPage) ? rawPage[0] : rawPage).trim();
  if (!/^[1-9][0-9]{0,3}$/.test(raw)) {
    return { page: 1, isPaginated: false, needsRedirect: true };
  }
  const page = Number(raw);
  if (page <= 1 || page > MAX_SEO_PAGE) {
    return { page: 1, isPaginated: false, needsRedirect: true };
  }
  return { page, isPaginated: true, needsRedirect: false };
}

/**
 * Translate the `page` param into the API's pagination params.
 * Page 1 is left untouched so the default (device specific) page size and the
 * existing first-render payload stay exactly as they are today.
 */
export function applySeoPaginationToQuery(reqQuery = {}, page = 1, options = {}) {
  // Curated (/s) and capsule (/cp) compose their query from the CMS response,
  // so their pagination params are not necessarily user input - those callers
  // pass dropRawPaginationParams: false to leave them alone.
  const { dropRawPaginationParams = true } = options;
  const query = { ...reqQuery };
  delete query[SEO_PAGE_PARAM];
  // `page` is the only param that may drive pagination. Raw `currentPage` /
  // `pageSize` in the URL are dropped: no internal link emits them (buildSearch
  // strips both), and honouring them would open a second, uncanonicalised
  // pagination space for crawlers to wander into.
  if (dropRawPaginationParams) {
    delete query.currentPage;
    delete query.pageSize;
  }
  if (page > 1) {
    query.currentPage = page - 1;
    query.pageSize = SEO_PAGE_SIZE;
  }
  return query;
}

/**
 * Build the URL of a page in the series, preserving every other query param
 * (filters, sort, store...) so filtered listings paginate too.
 * Page 1 is always the clean URL - never `?page=1`.
 */
export function buildSeoPageUrl(req = {}, page = 1) {
  const pathname = (req.originalUrl || '/').split('?')[0].split('#')[0] || '/';
  const query = stripNonCanonicalParams(req.query || {});
  delete query[SEO_PAGE_PARAM];
  // These are ignored on the way in, so never advertise them on the way out.
  delete query.currentPage;
  delete query.pageSize;
  if (page > 1) {
    query[SEO_PAGE_PARAM] = page;
  }
  const search = qs.stringify(query);
  return search ? `${pathname}?${search}` : pathname;
}

/**
 * Client side counterpart of buildSeoPageUrl: takes the current
 * `window.location.search` and returns the search string for `page`, leaving
 * every other param untouched. Page 1 drops the param.
 */
export function buildSeoPageSearch(currentSearch = '', page = 1) {
  const params = new URLSearchParams(currentSearch || '');
  if (page > 1) {
    params.set(SEO_PAGE_PARAM, String(page));
  } else {
    params.delete(SEO_PAGE_PARAM);
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * Total pages in the SEO series. Deliberately derived from totalResults and
 * SEO_PAGE_SIZE rather than from the API's `totalPages`: the latter reflects
 * the page size of the current request (15 on a mobile first render), which
 * would produce links to pages that do not line up with what `?page=N` serves.
 */
export function getSeoTotalPages(totalResults = 0) {
  const total = Number(totalResults) || 0;
  return total > 0 ? Math.ceil(total / SEO_PAGE_SIZE) : 0;
}

function buildPageLinks(req, page, totalPages) {
  const pages = new Set([1, totalPages]);
  for (let i = page - PAGE_WINDOW; i <= page + PAGE_WINDOW; i++) {
    if (i >= 1 && i <= totalPages) {
      pages.add(i);
    }
  }
  return [...pages]
    .sort((a, b) => a - b)
    .map(p => ({ page: p, url: buildSeoPageUrl(req, p), current: p === page }));
}

/**
 * Everything the views need to render the head links and the hidden nav.
 * Returns null when there is nothing to paginate.
 */
export function getSeoPaginationView({ req, page = 1, totalResults = 0 }) {
  const totalPages = getSeoTotalPages(totalResults);
  if (totalPages <= 1) {
    return null;
  }
  return {
    page,
    totalPages,
    prevUrl: page > 1 ? buildSeoPageUrl(req, page - 1) : '',
    nextUrl: page < totalPages ? buildSeoPageUrl(req, page + 1) : '',
    pageLinks: buildPageLinks(req, page, totalPages)
  };
}

/**
 * Crawlable product links for the products rendered on this page. The SSR
 * markup is a shimmer, so without these a crawler finds no PDP links at all -
 * pagination on its own would only expose empty shells.
 */
export function getSeoProductLinks({ results = [], entities = {} } = {}) {
  return results.reduce((acc, code) => {
    const entity = entities[code];
    const productUrl = entity && entity.url;
    if (!productUrl) {
      return acc;
    }
    const { brandName = '' } = entity.fnlColorVariantData || {};
    acc.push({
      url: productUrl,
      name: `${brandName} ${entity.name || ''}`.trim()
    });
    return acc;
  }, []);
}

/**
 * Append `?page=N` to an already built (query-stripped) canonical URL so every
 * component page self-canonicalises. Without this `?page=2` canonicalises to
 * page 1 and the whole series is dropped as duplicate content.
 */
export function withSeoPageCanonical(canonicalUrl = '', page = 1) {
  if (!canonicalUrl || page <= 1) {
    return canonicalUrl;
  }
  // /fb and /fc canonicalise on the full original URL, so the param may
  // already be there.
  if (new RegExp(`[?&]${SEO_PAGE_PARAM}=`).test(canonicalUrl)) {
    return canonicalUrl;
  }
  return `${canonicalUrl}${canonicalUrl.includes('?') ? '&' : '?'}${SEO_PAGE_PARAM}=${page}`;
}

/**
 * ` - Page N` suffix, so paginated pages do not share page 1's title.
 */
export function getSeoPageTitleSuffix(page = 1) {
  return page > 1 ? ` - Page ${page}` : '';
}

export default {
  SEO_PAGE_SIZE,
  SEO_PAGE_PARAM,
  parseSeoPage,
  applySeoPaginationToQuery,
  buildSeoPageUrl,
  buildSeoPageSearch,
  getSeoTotalPages,
  getSeoPaginationView,
  getSeoProductLinks,
  withSeoPageCanonical,
  getSeoPageTitleSuffix
};
